import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { HttpError } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { openDemoSession } from '../apiClient.ts';
import PublicPage from '../components/PublicPage.tsx';
import { FORGE_URL, PHONE_HREF, PHONE_LABEL } from './demoLinks.ts';

type Failure = 'busy' | 'unavailable';

const failureText: Record<Failure, { title: string; body: string }> = {
  busy: {
    title: 'The demo is busy right now.',
    body: 'Give it a few minutes and try again.'
  },
  unavailable: {
    title: 'The demo is not available right now.',
    body: `Try again shortly, or call Nick at ${PHONE_LABEL} for a walkthrough.`
  }
};

/** "Try the demo" lands here: a session from the API, then the dashboard. Nothing to type. */
const DemoStart = () => {
  const navigate = useNavigate();
  const [failure, setFailure] = useState<Failure | null>(null);

  const open = useCallback(async () => {
    setFailure(null);
    try {
      await openDemoSession();
      navigate('/', { replace: true });
    } catch (error) {
      setFailure(error instanceof HttpError && error.status === 429 ? 'busy' : 'unavailable');
    }
  }, [navigate]);

  useEffect(() => {
    void open();
  }, [open]);

  return (
    <PublicPage>
      {failure ? (
        <Box role="alert" sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 700 }}>{failureText[failure].title}</Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
            {failureText[failure].body}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 2.5, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => void open()}>
              Try again
            </Button>
            {failure === 'unavailable' ? (
              <Button variant="outlined" href={PHONE_HREF}>
                {PHONE_LABEL}
              </Button>
            ) : (
              <Button variant="outlined" href={FORGE_URL}>
                Back to Forge
              </Button>
            )}
          </Stack>
        </Box>
      ) : (
        <Box role="status" sx={{ mt: 3 }}>
          <CircularProgress size={28} />
          <Typography sx={{ mt: 1.5, fontWeight: 600 }}>Opening the demo workspace</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Sample clients, introductions and follow-ups, shared with other visitors and reset every
            hour.
          </Typography>
        </Box>
      )}
    </PublicPage>
  );
};

export default DemoStart;
